import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { goToPage } from "@/app/lib/util/utilities.client";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { UserDetailsModel } from "@/app/lib/models/UserDetailsModel";
import CookieSessionManager, {
  CookieData,
  CookiePayload,
} from "@/app/services/session.client";
import { stashLoginNext } from "@/app/lib/util/login-next-redirect.util";

function resolveCookieIdForRoute(currentUrl: string): string | undefined {
  if (
    currentUrl === AppConstants.RouteUrls.TRAINEE_CHAT ||
    currentUrl.startsWith(`${AppConstants.RouteUrls.TRAINEE_CHAT}/`)
  ) {
    return AppConstants.Roles.TRAINEE;
  }
  return undefined;
}

function redirectToLoginWithNext(
  router: ReturnType<typeof useRouter>,
  nextPath: string,
) {
  stashLoginNext(nextPath);
  const loginUrl = `${AppConstants.RouteUrls.LOGIN}?next=${encodeURIComponent(nextPath)}`;
  // Hard navigation so ?next= is reliably present for LoginPage / useSearchParams.
  if (typeof window !== "undefined") {
    window.location.assign(loginUrl);
    return;
  }
  goToPage(router, loginUrl);
}

const useInitializeUserFromCookie = (
  currentUrl: string,
): UserDetailsModel | undefined => {
  const router = useRouter();
  const [userDetails, setUserDetails] = useState<UserDetailsModel | undefined>(
    undefined,
  );
  /**
   * Check if a user is logged in on the basis of the browser cookie (set during login) and the allowed URLs from the cookie
   * @param cookiePayload
   * @param  router
   * @param {string} currentURL
   */
  const checkLoggedInStatus = useCallback(
    async (cookiePayload: CookiePayload, router, currentURL: string) => {
      let newUrl = AppConstants.RouteUrls.LOGIN;
      // Check if the loginCookieValue is not undefined or not empty or has allowedURLs if not then redirect to login page
      if (cookiePayload && cookiePayload.email && cookiePayload.allowedURLs) {
        if (cookiePayload.allowedURLs) {
          if (
            checkIfURLisAllowedForUser(currentURL, cookiePayload.allowedURLs)
          ) {
            newUrl = currentURL;
          }
        }
      } else {
        const loggedOut = await CookieSessionManager.doLogout();
        if (loggedOut) {
          redirectToLoginWithNext(router, currentURL);
        }
      }
    },
    [],
  );

  const memoizedCheckLoggedInStatus = useCallback(
    (cookiePayload: CookiePayload, cookieData: CookieData) => {
      checkLoggedInStatus(cookiePayload, router, currentUrl);
      const normalizedRole =
        cookiePayload.role?.trim().toLowerCase().replace(/\s+/g, "_") ||
        cookiePayload.role;
      const userDetailsFromCookie = {
        email_id: cookiePayload.email,
        role: normalizedRole,
        first_name: cookiePayload.firstName,
        last_name: cookiePayload.lastName,
        login_session_id:
          cookiePayload.sessionId || cookieData.name || normalizedRole,
        img_url: cookiePayload.user_img_url,
        account_type: cookiePayload.account_type,
        premium_data_filled: false,
        trial_end_date: cookiePayload.trial_end_date,
        premium_end_date: cookiePayload.premium_end_date,
        plan_tier: cookiePayload.plan_tier,
        plan_end_date: cookiePayload.plan_end_date as any,
        introductory_expires_at: cookiePayload.introductory_expires_at as any,
        entitlement_boost_tier: cookiePayload.entitlement_boost_tier,
        entitlement_boost_until: cookiePayload.entitlement_boost_until as any,
        has_center_head_link: cookiePayload.has_center_head_link,
        registration_date_ms: cookiePayload.registration_date_ms,
        preferred_currency: cookiePayload.preferred_currency,
      };
      if (userDetailsFromCookie.role === AppConstants.Roles.GUARDIAN) {
        userDetailsFromCookie.premium_data_filled =
          cookiePayload.premium_data_filled;
      }
      setUserDetails(userDetailsFromCookie);
    },
    [checkLoggedInStatus, currentUrl, router],
  );

  /**
   * Check if the current URL is allowed for the user
   * @param currentUrl
   * @param allowedUrls
   */
  const checkIfURLisAllowedForUser = (
    currentUrl: string,
    allowedUrls: string[],
  ): boolean => {
    if (!Array.isArray(allowedUrls) || allowedUrls.length === 0) return false;
    if (allowedUrls.includes(currentUrl)) {
      return true;
    }
    const urlSegments = currentUrl.split("/");
    for (const allowedUrl of allowedUrls) {
      const allowedSegments = allowedUrl.split("/");
      if (allowedSegments.length === urlSegments.length) {
        let isMatch = true;
        for (let i = 0; i < allowedSegments.length; i++) {
          if (
            allowedSegments[i] !== "*" &&
            allowedSegments[i] !== urlSegments[i]
          ) {
            isMatch = false;
            break;
          }
        }
        if (isMatch) return true;
      }
    }
    return false;
  };

  useEffect(() => {
    // 🔴 FIX 1: Add this GUARD CLAUSE at the very top.
    // If we are on an automation/test page, STOP here. Do not run any logic.
    // Let the AutomationSignInPage handle its own state.
    if (
      currentUrl.includes("/login/test") ||
      currentUrl.includes("/login/payments") ||
      currentUrl.includes("/login/quiz") ||
      currentUrl.includes("/register") ||
      currentUrl.startsWith("/access-link")
    ) {
      return;
    }

    const loadUserFromCookie = () => {
      try {
        const cookieId = resolveCookieIdForRoute(currentUrl);
        const cookieData = CookieSessionManager.getCookieOrSession(
          router,
          cookieId,
        ) as CookieData | undefined;
        if (!cookieData?.value) {
          throw new Error("User is not authenticated");
        }
        const cookiePayload = cookieData.value as CookiePayload;
        if (cookiePayload && cookiePayload.email && cookiePayload.role) {
          memoizedCheckLoggedInStatus(cookiePayload, cookieData);
        } else {
          throw new Error("User is not authenticated");
        }
      } catch (error) {
        const isLoginRoute =
          currentUrl === AppConstants.RouteUrls.LOGIN ||
          currentUrl === "/login" ||
          currentUrl.startsWith("/login/");
        const isRegisterRoute = currentUrl.includes(
          AppConstants.RouteUrls.REGISTER,
        );

        if (!isLoginRoute && !isRegisterRoute) {
          redirectToLoginWithNext(router, currentUrl);
        }
      }
    };

    loadUserFromCookie();

    const handleCookieUpdated = () => {
      loadUserFromCookie();
    };

    window.addEventListener("user-cookie-updated", handleCookieUpdated);
    return () => {
      window.removeEventListener("user-cookie-updated", handleCookieUpdated);
    };
  }, [currentUrl, memoizedCheckLoggedInStatus, router]);

  return userDetails;
};

export default useInitializeUserFromCookie;
